import MDAlert from "components/MDAlert";
import { SelectOptions } from "components/Shared/CustomSelect/CustomSelect";
import useGetPlants from "hooks/organization/plants/useGetPlants";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import parseGraphQLError from "utils/graphQL/parseGraphQLError";
import usePaginatedResources, { Pagination } from "hooks/usePaginatedResources";
import { mapPlantProductTypeOptions } from "constants/PlantProductTypes";
import { Plant, ProposalPlant } from "generated/graphql";
import { useMemo } from "react";

export type PlantOptionPlant = Pick<Plant, "id" | "name"> & {
  products?: Plant["products"];
};

export default function PlantOptions({
  children,
  selectedPlant = null,
}: {
  selectedPlant?: Plant | ProposalPlant | PlantOptionPlant | null;
  children: (props: {
    plantOptions: SelectOptions;
    plantProductOptions: Record<SelectOptions[number]["value"], SelectOptions>;
    loading: boolean;
    plantsWithProductsOptions: SelectOptions;
    ProductsOptionsByPlant: (v: string) => SelectOptions;
    search: (v: string) => void;
    pagination: Pagination;
    plants?: Plant[];
  }) => React.ReactNode;
}) {
  const { filtering, offset } = usePaginatedVariables();

  const { loading, data, error, paginatorInfo } = useGetPlants({
    first: offset.first,
    search: filtering.debouncedSearchTerm,
    page: offset.page,
  });

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo,
  });

  const plants = useMemo<Array<Plant | ProposalPlant | PlantOptionPlant>>(() => {
    const loadedPlants = data ?? [];
    const selected = selectedPlant ?? null;

    const selectedPlantExists =
      !!selected && loadedPlants.some((plant) => plant.id === selected.id);

    if (!selectedPlantExists && selected) {
      return [selected, ...loadedPlants];
    }

    return loadedPlants;
  }, [data, selectedPlant]);

  const fullPlants = useMemo(
    () =>
      plants.filter(
        (plant): plant is Plant =>
          "addressLine1" in plant &&
          "addressCity" in plant &&
          "addressState" in plant &&
          "addressZip" in plant &&
          "addressCountry" in plant
      ),
    [plants]
  );

  const plantOptions = plants
    .map((plant) => ({
      label: plant.name,
      value: plant.id,
    }));

  const getProducts = (plant: Plant | ProposalPlant | PlantOptionPlant): Plant["products"] => {
    return "products" in plant && Array.isArray(plant.products) ? plant.products : [];
  };

  const plantProductOptions = plants.reduce<Record<string, SelectOptions>>((acc, val) => {
    const products = getProducts(val);
    return {
      ...acc,
      [val.id]: products.map((plantProduct) => ({
        label: mapPlantProductTypeOptions(plantProduct.type),
        value: plantProduct.id,
      })),
    };
  }, {});

  const plantsWithProductsOptions = plants.reduce<SelectOptions>((acc, val) => {
    const products = getProducts(val);
    return [
      ...acc,
      ...products.map((plantProduct) => ({
        label: `${val.name} - ${mapPlantProductTypeOptions(plantProduct.type)}`,
        value: plantProduct.id,
      })),
    ];
  }, []);

  const ProductsOptionsByPlant = (plantId: string) => {
    return plants.reduce<SelectOptions>((acc, val) => {
      const products = getProducts(val);
      if (val?.id == plantId) {
        return [
          ...acc,
          ...products.map((plantProduct) => ({
            label: `${mapPlantProductTypeOptions(plantProduct.type)}`,
            value: plantProduct.id,
          })),
        ];
      } else {
        return [...acc];
      }
    }, []);
  };

  if (error) {
    return <MDAlert color="error">{parseGraphQLError(error)}</MDAlert>;
  }
  return (
    <>
      {children({
        plantOptions,
        loading,
        search: filtering.setSearchTerm,
        plantProductOptions,
        plantsWithProductsOptions,
        ProductsOptionsByPlant,
        pagination,
        plants: fullPlants,
      })}
    </>
  );
}
